import { FC, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { FieldArray, Formik } from 'formik';
import { AiOutlinePlusCircle } from 'react-icons/ai';

import {
    BANKING_DETAILS_INITIAL_VALUES,
    OUTLET_INITIAL_VALUES,
} from 'src/constants/formInitialValues.ts/addMerchantStep3';
import { outletInformationValidations } from 'src/constants/formValidations/addMerchantOutletStep';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { getCountryName } from 'src/lib/countryConfig';
import getPostalLabel from 'src/lib/postalLabel';
import { Outlet, ServiceChargeCalculatedAt } from 'src/types/AddMerchant/merchantInfo';

import OutletInformationComponent from './components/OutletInformation';

interface OutletInformationProps {
    handleBack: () => void;
    isServiceApplication?: boolean;
}

const OutletInformation: FC<OutletInformationProps> = ({
    handleBack,
    isServiceApplication = false,
}) => {
    const { addMerchant, merchantApplication } = useAddMerchantContext();

    const onSubmit = async (input) => {
        // Ensure no banking details are saved if the service charge level is not at the outlet level
        const outlets = input.outlets.map((outlet) => {
            if (
                outlet.bankingDetails?.serviceChargeCalculatedAt !==
                ServiceChargeCalculatedAt.OUTLETLEVEL
            ) {
                return {
                    ...outlet,
                    bankingDetails: {
                        ...BANKING_DETAILS_INITIAL_VALUES,
                        serviceChargeCalculatedAt: outlet.bankingDetails.serviceChargeCalculatedAt,
                    },
                };
            }
            return outlet;
        });

        const updatedApplication = {
            ...(merchantApplication?.merchantInformation as object),
            numberOfOutlets: input.numberOfOutlets,
            outlets: [...outlets],
        };

        await addMerchant({
            merchantInformation: updatedApplication,
            outletInformation: { isStepCompleted: true },
        });
    };

    const outlets = useMemo(() => {
        const numberOfOutlets = merchantApplication?.merchantInformation?.numberOfOutlets ?? 2;

        const existingOutlets =
            merchantApplication?.merchantInformation?.outlets ?? ([] as Outlet[]);

        const newOutlets_formValues = Array.from({
            length: numberOfOutlets - existingOutlets.length,
        }).map(() => {
            return {
                ...OUTLET_INITIAL_VALUES,
                tradingAddress: {
                    country: getCountryName(
                        merchantApplication?.partner?.countryDetails?.alpha2Code
                    ),
                },
            };
        });

        return [...existingOutlets, ...newOutlets_formValues];
    }, [merchantApplication]);

    return (
        <Formik
            initialValues={{
                outlets,
                isStepCompleted: merchantApplication?.outletInformation?.isStepCompleted ?? false,
                numberOfOutlets: merchantApplication?.merchantInformation?.numberOfOutlets ?? 2,
            }}
            validationSchema={outletInformationValidations(
                getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code)
            )}
            onSubmit={onSubmit}
            validateOnChange
            enableReinitialize
            validateOnMount={false}
        >
            {(formikProps) => {
                return (
                    <form>
                        <FieldArray name="outlets">
                            {({ push, remove, form }) => {
                                return (
                                    <Box display="flex" flexDirection="column" gap="2rem">
                                        {form.values.outlets.map((_, index) => {
                                            return (
                                                <OutletInformationComponent
                                                    key={index}
                                                    index={index}
                                                    formik={formikProps}
                                                    remove={remove}
                                                    isServiceApplication={isServiceApplication}
                                                />
                                            );
                                        })}
                                        {!isServiceApplication && (
                                            <Box display="flex" justifyContent="flex-end">
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    marginTop="24px"
                                                    onClick={() => {
                                                        push(OUTLET_INITIAL_VALUES);
                                                        formikProps.setFieldValue(
                                                            'numberOfOutlets',
                                                            formikProps.values.numberOfOutlets + 1
                                                        );
                                                    }}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <AiOutlinePlusCircle
                                                        color="#F3A256"
                                                        size="24px"
                                                    />
                                                    <Typography
                                                        fontSize="16px"
                                                        color="#575757"
                                                        lineHeight="24px"
                                                        fontWeight="400"
                                                        marginLeft="8px"
                                                    >
                                                        Add Outlet
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            }}
                        </FieldArray>
                        {!isServiceApplication && (
                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    sx={{
                                        alignSelf: 'end',
                                        marginTop: '24px',
                                        marginRight: '22px',
                                    }}
                                    onClick={handleBack}
                                    variant="back-button"
                                >
                                    Back
                                </Button>

                                <Button
                                    sx={{
                                        alignSelf: 'end',
                                        marginTop: '24px',
                                    }}
                                    onClick={() => {
                                        return formikProps.handleSubmit();
                                    }}
                                    variant="main"
                                >
                                    Next
                                </Button>
                            </Box>
                        )}
                    </form>
                );
            }}
        </Formik>
    );
};

export default OutletInformation;
